import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase, NotificationsResponse } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { RealtimeChannel } from "@supabase/supabase-js";

/**
 * Global real-time listener for new notifications.
 * Mount in DashboardLayout so ALL logged-in users get instant updates.
 */
export const useGlobalNotificationListener = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  }, []);

  const invalidateNotifications = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["notifications"] });
    queryClient.invalidateQueries({ queryKey: ["notifications-count"] });
    queryClient.invalidateQueries({ queryKey: ["notification-read-status"] });
  }, [queryClient]);

  const setupChannel = useCallback(() => {
    cleanup();
    retryCountRef.current = 0;

    const channel = supabase
      .channel("global-notifications-listener")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications" },
        (payload) => {
          const newNotification = payload.new as NotificationsResponse;

          // Skip if current user created this notification (they already see a success toast)
          if (user?.id && newNotification.user_id === user.id) {
            // Still invalidate caches so counts update
            invalidateNotifications();
            return;
          }

          // Invalidate all notification caches
          invalidateNotifications();

          // Show toast
          if (!newNotification.archived && !newNotification.is_deleted) {
            toast.info(`Nieuwe melding: ${newNotification.type}`, {
              description:
                newNotification.message.substring(0, 50) +
                (newNotification.message.length > 50 ? "..." : ""),
            });
          }
        }
      )
      .subscribe((status) => {
        console.log(`[GlobalNotif] Channel status: ${status}`);
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          handleReconnect();
        }
        if (status === "SUBSCRIBED") {
          retryCountRef.current = 0;
        }
      });

    channelRef.current = channel;
  }, [cleanup, user?.id, invalidateNotifications]);

  const handleReconnect = useCallback(() => {
    const delay = Math.min(1000 * Math.pow(2, retryCountRef.current), 30000);
    retryCountRef.current++;
    console.log(`[GlobalNotif] Reconnecting in ${delay}ms (attempt ${retryCountRef.current})`);

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    reconnectTimeoutRef.current = setTimeout(() => {
      setupChannel();
      invalidateNotifications();
    }, delay);
  }, [setupChannel, invalidateNotifications]);

  useEffect(() => {
    setupChannel();

    const handleSessionRestored = () => {
      console.log("[GlobalNotif] Session restored, rebuilding channel");
      setupChannel();
      invalidateNotifications();
    };

    window.addEventListener("session-restored", handleSessionRestored);

    return () => {
      window.removeEventListener("session-restored", handleSessionRestored);
      cleanup();
    };
  }, [setupChannel, invalidateNotifications, cleanup]);
};
